import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errors'
import { AidStation, CreateAidStation, UpdateAidStation } from '../types'

export function useAidStations(runId: string) {
  return useQuery({
    queryKey: ['aid_stations', runId],
    queryFn: async (): Promise<AidStation[]> => {
      const { data, error } = await supabase
        .from('aid_stations')
        .select('*')
        .eq('run_id', runId)
        .order('at_distance_km')
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!runId
  })
}

export function useCreateAidStation() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (newAidStation: CreateAidStation): Promise<AidStation> => {
      const { data, error } = await supabase
        .from('aid_stations')
        .insert(newAidStation)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['aid_stations', data.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs', data.run_id] })
    }
  })
}

export function useUpdateAidStation() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateAidStation }): Promise<AidStation> => {
      const { data, error } = await supabase
        .from('aid_stations')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['aid_stations', data.run_id] })
      queryClient.invalidateQueries({ queryKey: ['runs', data.run_id] })
    }
  })
}

export function useDeleteAidStation() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      // Get the aid station first to know which run to invalidate
      const { data: aidStation } = await supabase
        .from('aid_stations')
        .select('run_id')
        .eq('id', id)
        .single()
      
      const { error } = await supabase
        .from('aid_stations')
        .delete()
        .eq('id', id)
      
      if (error) throw handleSupabaseError(error)
      return aidStation!.run_id
    },
    onSuccess: (runId) => {
      queryClient.invalidateQueries({ queryKey: ['aid_stations', runId] })
      queryClient.invalidateQueries({ queryKey: ['runs', runId] })
    }
  })
}